import {useState, useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {TState} from '../redux/store.type';
import {getEventSettings} from '../redux/event/eventActions';
import {UseSendDataConfig, SendData} from './sendHooks.types';
import {useDefaultFbImage} from './useDefaultFbImage';
import {getWhatsappTemplate} from '../redux/branding/brandingActions';
export const useSendData = ({defaultMessage, defaultSubject, defaultEmailBody, defaultSmsMessage, type, hasWhatsapp = true}: UseSendDataConfig) => {
    const savedData = useSelector((s: TState) => type ? s.mailing.designMailing?.[type] : null);
    const getEventSettingsXHR = useSelector((s: TState) => s.event.getEventSettingsXHR);
    const whatsappTemplate = type && hasWhatsapp ? useSelector((s: TState) => s.branding[`${type}WhatsappTemplate`]) : null;
    const defaultWhatsappImage = useDefaultFbImage(true);
    const defaultEmailImage = useDefaultFbImage(false);

    const [whatsappData, setWhatsappData] = useState({
        image: defaultWhatsappImage,
        message: savedData?.whatsappContent || defaultMessage,
        whatsappTemplate: null as typeof whatsappTemplate
    });

    const [emailData, setEmailData] = useState({
        image: defaultEmailImage,
        emailBody: savedData?.emailContent || defaultEmailBody,
        emailSubject: savedData?.emailSubject || defaultSubject
    });

    const [smsData, setSmsData] = useState({message: savedData?.smsContent || defaultSmsMessage});

    const handleDataSave = useCallback((data: unknown, key: string, type: keyof SendData) => {
        const setters = {
            whatsapp: setWhatsappData,
            email: setEmailData,
            sms: setSmsData
        };
        
        const setter = setters[type];
        if(setter) {
            setter(prev => ({...prev, [key]: data}));
        }
    }, []);

    // Initialize event settings
    useEffect(() => {
        if(!getEventSettingsXHR) {
            getEventSettings().then(); 
        }
        if(type && !whatsappTemplate && hasWhatsapp) {
            getWhatsappTemplate(type).then();
        }
    }, [getEventSettingsXHR, type, whatsappTemplate, hasWhatsapp]);

    // Update image when design data becomes available
    useEffect(() => {
        if(defaultWhatsappImage.path !== whatsappData.image.path) 
            setWhatsappData(prev => ({...prev, image: defaultWhatsappImage}));
        if(defaultEmailImage.path !== emailData.image.path)
            setEmailData(prev => ({...prev, image: defaultEmailImage}));
    }, [defaultWhatsappImage.path, defaultEmailImage.path]);

    // Sync savedData from DB when it loads
    useEffect(() => {
        if(!savedData) return;
        if(savedData.emailContent) {
            setEmailData(prev => ({
                ...prev,
                emailBody: savedData.emailContent!,
                ...(savedData.emailSubject ? {emailSubject: savedData.emailSubject} : {})
            }));
        }
        if(savedData.smsContent) {
            setSmsData({message: savedData.smsContent});
        }
        if(savedData.whatsappContent) {
            const parsed = JSON.parse(savedData.whatsappContent);
            if(Array.isArray(parsed)) {
                setWhatsappData(prev => ({...prev, whatsappTemplate: parsed}));
            }
        }
    }, [savedData]);

    const effectiveWhatsappTemplate = whatsappData.whatsappTemplate || whatsappTemplate;
    return {whatsappData: {...whatsappData, whatsappTemplate: effectiveWhatsappTemplate}, emailData, smsData, handleDataSave, whatsappTemplate};
};
